'use client';

import * as React from 'react';
import { XIcon } from 'lucide-react';

import { Command as CommandPrimitive } from 'cmdk';
import { Command, CommandGroup, CommandItem } from './Command';
import { Badge } from './Badge';
import { InfoIconTooltip } from './InfoTooltip';
import { LoadingSpinner } from './LoadingSpinner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './Tooltip';
import { useEffect } from 'react';

export type SelectType = {
  value: string;
  label: string;
  help?: string;
};

type MultiSelectProps = {
  value: SelectType[];
  onChange: (value: SelectType[]) => void;
  availableSelectables: SelectType[];
  placeholder: string;
  isLoading?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onOpenChange?: (open: boolean) => void;
  /** Accessible name for the input (cmdk renders it as a visually hidden label). */
  label?: string;
};

export function FancyMultiSelect({
  value,
  onChange,
  availableSelectables,
  placeholder,
  isLoading = false,
  disabled = false,
  disabledReason = '',
  onOpenChange,
  label,
}: MultiSelectProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);

  const [inputValue, setInputValue] = React.useState('');

  const safeValue = React.useMemo(() => {
    // Ensure that value is always an array
    return Array.isArray(value) ? value : [];
  }, [value]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
      setInputValue('');
    }
  }, [disabled]);

  const handleUnselect = React.useCallback(
    (selectType: SelectType) => {
      if (disabled) return; // Don't allow removal when disabled
      onChange(safeValue.filter((s) => s.value !== selectType.value)); // No need for ternary
    },
    [safeValue, onChange, disabled],
  );

  const handleDropdownStateChange = (open: boolean) => {
    onOpenChange?.(open);
  };

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (input) {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          if (input.value === '' && !disabled) {
            onChange(safeValue.slice(0, -1));
          }
        }
        // This is not a default behaviour of the <input /> field
        if (e.key === 'Escape') {
          input.blur();
        }
      }
    },
    [safeValue, onChange, disabled],
  );

  const selectables: SelectType[] = React.useMemo(() => {
    return availableSelectables.filter(
      (selectable) =>
        !safeValue.some(
          (selectedItem) => selectedItem.value === selectable.value,
        ),
    );
  }, [availableSelectables, safeValue]);

  // The dropdown renders inline (not in a portal), so when it opens near the
  // bottom of a scroll container (e.g. inside a dialog) its options can be
  // clipped with no scroll cue. Reveal it in the nearest scrollable ancestor.
  React.useEffect(() => {
    if (!open || !dropdownRef.current) return;
    const frame = requestAnimationFrame(() => {
      dropdownRef.current?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, selectables.length]);

  return (
    <Command
      label={label}
      onKeyDown={handleKeyDown}
      className="overflow-visible bg-transparent"
      onFocus={() => {
        handleDropdownStateChange(true);
      }}
      onBlur={() => {
        handleDropdownStateChange(false);
      }}
    >
      <div className="group border border-input px-3 py-2 text-sm rounded-md shadow-xs focus-within:outline-none focus-within:ring-1 focus-within:ring-ring">
        <div className="flex gap-1 flex-wrap items-center min-h-[1.375rem]">
          {safeValue.map((selectable) => {
            return (
              <Badge
                key={selectable.value}
                variant="secondary"
                className="max-w-[300px]"
              >
                <div className="flex items-center">
                  <span className="truncate">{selectable.label}</span>
                  {selectable.help && (
                    <div className="ml-2 h-3 w-3 flex-shrink-0">
                      <InfoIconTooltip>{selectable.help}</InfoIconTooltip>
                    </div>
                  )}
                  {!disabled && (
                    <button
                      className="ml-1 ring-offset-background rounded-full outline-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleUnselect(selectable);
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => handleUnselect(selectable)}
                    >
                      <XIcon className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                    </button>
                  )}
                </div>
              </Badge>
            );
          })}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex-1">
                  <CommandPrimitive.Input
                    ref={inputRef}
                    disabled={disabled}
                    value={inputValue}
                    onValueChange={setInputValue}
                    onBlur={() => setOpen(false)}
                    onFocus={() => setOpen(true)}
                    placeholder={placeholder}
                    className="ml-2 bg-transparent outline-none placeholder:text-muted-foreground w-full"
                  />
                </div>
              </TooltipTrigger>
              {disabled && (
                <TooltipContent>
                  <p>{disabledReason}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <div className="relative mt-2">
        {open && (
          <div
            className="absolute w-full z-10 top-0 rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in"
            ref={dropdownRef}
          >
            {!isLoading && availableSelectables.length === 0 && (
              <div className="py-6 text-center text-sm">No selectables</div>
            )}
            {!isLoading &&
              availableSelectables.length > 0 &&
              selectables.length === 0 && (
                <div className="py-6 text-center text-sm">
                  No selectables matching filter
                </div>
              )}
            {isLoading && <LoadingSpinner size={'small'}></LoadingSpinner>}
            <CommandGroup className="h-full overflow-auto">
              {selectables.map((selectable) => {
                return (
                  <CommandItem
                    key={selectable.value}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onSelect={() => {
                      setInputValue('');
                      onChange([...safeValue, selectable]);
                    }}
                    className={'cursor-pointer'}
                  >
                    <div className="flex items-center max-w-full">
                      <span className="truncate max-w-[300px]">
                        {selectable.label}
                      </span>
                      {selectable.help && (
                        <div className="ml-2 size-4 flex-shrink-0">
                          <InfoIconTooltip>{selectable.help}</InfoIconTooltip>
                        </div>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </div>
        )}
      </div>
    </Command>
  );
}
